import { ref, onUnmounted } from 'vue'
import type { Router } from 'vue-router'
import type { Store } from 'vuex'
import { useAdminDB } from '@/composables/useAdminDB'
import { performLogout, withTimeout, LOGOUT_NETWORK_TIMEOUT_MS } from '@/composables/useAutoLogout'
import { updateAvailability } from '@/composables/useAvailability'
import type { AuthUser } from '@/types/auth'

const WARNING_SECONDS = 30

// ── Estado de módulo (singleton) — exportado direto para o modal poder
// consumir sem re-chamar useForceLogout(). ──
export const showForceLogout = ref(false)
export const forceLogoutSecondsLeft = ref(WARNING_SECONDS)
// 'account' = pedido via Admin → Últimos Acessos (kick de TODAS as abas do
// usuário, fila em agent_force_logout). 'session' = revoke de UMA sessão
// específica via Admin → Sessões (só a aba dona daquele session_id é afetada
// — ver useSessionTracking.ts, migração v30). Mesmo popup de aviso pros dois
// casos (ForceLogoutWarningModal.vue), só muda o que "Continuar logado" limpa.
export const forceLogoutKind = ref<'account' | 'session'>('account')

let countdownTimer: ReturnType<typeof setInterval> | null = null

function stopCountdown() {
  if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null }
}

/**
 * Apaga o pedido de logout no banco (migração v21) — chamado tanto ao
 * revogar ("Continuar logado") quanto ao efetivar o logout, senão o próximo
 * login do agente cairia direto no popup de novo.
 */
async function clearPendingRequest(userId: number) {
  try {
    await useAdminDB().clearForceLogout(userId)
  } catch (err) {
    console.warn('[ForceLogout] clearForceLogout:', err)
  }
}

// Callback injetado por useSessionTracking.ts pra revogar SÓ a sessão atual
// (agent_sessions.revoked_at) quando forceLogoutKind === 'session' — sem
// import direto pra não fechar um ciclo de 3 módulos (useForceLogout ↔
// useAutoLogout ↔ useSessionTracking) mais difícil de acompanhar; o ciclo de
// 2 (useForceLogout ↔ useAutoLogout, performLogout/resetForceLogout) já
// existe hoje e funciona — este é só mais indireto de propósito.
let sessionRevokeResolver: (() => Promise<void>) | null = null
export function setSessionRevokeResolver(fn: (() => Promise<void>) | null): void {
  sessionRevokeResolver = fn
}

/**
 * Arma o popup de aviso (30s, cancelável) — chamado pelo heartbeat de sessão
 * (useSessionTracking.ts).
 * Idempotente: se já tem um aviso ativo, não reinicia a contagem.
 */
export function armForceLogoutWarning(kind: 'account' | 'session', store: Store<any>, router: Router): void {
  if (showForceLogout.value) return
  forceLogoutKind.value = kind
  showForceLogout.value = true
  forceLogoutSecondsLeft.value = WARNING_SECONDS
  stopCountdown()
  countdownTimer = setInterval(() => {
    forceLogoutSecondsLeft.value -= 1
    if (forceLogoutSecondsLeft.value <= 0) {
      logoutNowForced(store, router)
    }
  }, 1_000)
}

/**
 * Fase 4.2 do plano de otimização de login (docs/planos/PLANO-OTIMIZACAO-LOGIN.md):
 * chamado no logout — cobre um caso de corrida real: se um admin acabou de
 * pedir o kick de A (showForceLogout=true, countdown rodando) no exato
 * momento em que A também clica em "sair" por conta própria, sem isso o
 * countdown continuaria e chamaria logoutNowForced() de novo depois — dessa
 * vez efetivando logout no agente B, que acabou de logar na mesma aba.
 */
export function resetForceLogout(): void {
  showForceLogout.value = false
  forceLogoutSecondsLeft.value = WARNING_SECONDS
  forceLogoutKind.value = 'account'
  stopCountdown()
}

/**
 * Botão "Continuar logado" do popup — revoga o pedido do admin e restaura o
 * estado de "presente" na dashboard, o mesmo que um login normal grava:
 * último acesso (logAccess) e status online (updateAvailability, que também
 * reflete em Chatwoot + agent_status). Sem isso, o agente confirmava que
 * continuava logado mas Últimos Acessos/aba Agentes continuavam mostrando o
 * status "offline" deixado pelo pedido de logout do admin.
 */
export async function continueForcedSession(store: Store<any>) {
  const user = store.getters.authUser as AuthUser | null
  const kind = forceLogoutKind.value
  showForceLogout.value = false
  stopCountdown()
  forceLogoutSecondsLeft.value = WARNING_SECONDS
  forceLogoutKind.value = 'account'
  if (!user) return

  if (kind === 'session') {
    try {
      await sessionRevokeResolver?.()
    } catch (err) {
      console.warn('[ForceLogout] clearSessionRevoke:', err)
    }
  } else {
    await clearPendingRequest(user.id)
  }
  useAdminDB().logAccess(user.id, user.email, user.name)
    .catch(err => console.warn('[ForceLogout] logAccess:', err))
  updateAvailability(user.access_token, 'online', user.id)
    .catch(err => console.warn('[ForceLogout] updateAvailability:', err))
}

/**
 * Botão "Deslogar" (ou fim da contagem de 30s) — efetiva o logout.
 * clearPendingRequest() já tem seu próprio try/catch, mas isso só cobre
 * REJEIÇÃO — um fetch sem timeout que nunca resolve nem rejeita (mesmo risco
 * de performLogout() contra o Chatwoot self-hosted, ver useAutoLogout.ts)
 * ainda deixaria isto pendurado antes de chegar no performLogout() abaixo.
 */
export async function logoutNowForced(store: Store<any>, router: Router) {
  const user = store.getters.authUser as AuthUser | null
  showForceLogout.value = false
  stopCountdown()
  if (user) await withTimeout(clearPendingRequest(user.id), LOGOUT_NETWORK_TIMEOUT_MS)
  await performLogout(store, router)
}

/**
 * Chamado uma única vez, no setup() de App.vue — mesmo padrão de
 * useAutoLogout(). O polling de check_force_logout (pedido de logout da conta
 * inteira via Admin → Últimos Acessos, a cada 15s) foi retirado pra reduzir as
 * chamadas ao n8n; o popup agora só é armado pela revogação de sessão no
 * heartbeat (useSessionTracking.ts).
 */
export function useForceLogout() {
  onUnmounted(() => {
    stopCountdown()
  })
}
